/**
 * Hook for fetching a single initiative by ID
 * @module hooks/use-initiative-detail
 */

import { useState, useEffect, useCallback } from 'react';
import { getInitiativeById } from '@/lib/api/initiatives';
import type { Initiative } from '@/types/initiative';

export interface UseInitiativeDetailReturn {
  initiative: Initiative | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useInitiativeDetail(id: string): UseInitiativeDetailReturn {
  const [initiative, setInitiative] = useState<Initiative | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchInitiative = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      setError(null);
      const response = await getInitiativeById(id);
      if (response.success && response.data) {
        setInitiative(response.data);
      } else {
        setError(response.message || 'Failed to load initiative');
        setInitiative(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
      setInitiative(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchInitiative();
  }, [fetchInitiative]);

  return { initiative, loading, error, refetch: fetchInitiative };
}
